/**
 * Shared Theme Store
 *
 * A single Zustand theme store shared across all 6Tool apps.
 * Theme preference is persisted in localStorage and synchronized across all apps.
 *
 * Usage:
 * ```typescript
 * import { useThemeStore } from '@6tool/shared/stores';
 *
 * function MyComponent() {
 *   const { theme, toggleTheme } = useThemeStore();
 *   return <button onClick={toggleTheme}>{theme}</button>;
 * }
 * ```
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark';

interface ThemeStore {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

/**
 * Shared theme store used by all 6Tool apps
 * Theme changes in any app will be reflected in all other apps
 */
export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: 'dark',
      setTheme: (theme) => set({ theme }),
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'light' ? 'dark' : 'light',
        })),
    }),
    {
      name: '6tool-theme-storage',
    }
  )
);
